import { Subject } from '../common/enums';
import type { TransformerConfig } from '../common/types';
import { mergeMissedMarks } from '../legacyExamAttempts/utils';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { StudentSubjectAnalyticsAttributes } from './constants';
import { DBStudentSubjectAnalyticsSchema, StudentSubjectAnalyticsSchema } from './schemas';

/**
 * Codec configuration for StudentSubjectAnalytics entity.
 *
 * Transformations:
 * - Client `subject` (Subject enum) ↔ DB `subjectTopicIdentifier` (format: "SUBJECT#{subject}")
 */
export const StudentSubjectAnalyticsCodecConfig: TransformerConfig<
  typeof StudentSubjectAnalyticsSchema,
  typeof DBStudentSubjectAnalyticsSchema
> = {
  name: 'StudentSubjectAnalytics',
  clientSchema: StudentSubjectAnalyticsSchema,
  dbSchema: DBStudentSubjectAnalyticsSchema,
  transformers: [
    // studentId ↔ student
    createTransformer<typeof StudentSubjectAnalyticsSchema, typeof DBStudentSubjectAnalyticsSchema>(
      {
        encoder: {
          sourceFields: ['studentId'] as const,
          encode: ({ studentId }) => ({
            student: studentId,
          }),
        },
        decoder: {
          sourceFields: ['student'] as const,
          decode: ({ student }) => ({
            studentId: student,
          }),
        },
      }
    ),

    // subject: Subject enum ↔ subjectTopicIdentifier: "SUBJECT#{subject}"
    createTransformer<typeof StudentSubjectAnalyticsSchema, typeof DBStudentSubjectAnalyticsSchema>(
      {
        encoder: {
          sourceFields: ['subject'] as const,
          encode: ({ subject }) => ({
            subjectTopicIdentifier: `SUBJECT#${subject}`,
          }),
        },
        decoder: {
          sourceFields: ['subjectTopicIdentifier'] as const,
          decode: ({ subjectTopicIdentifier }) => {
            const parts = subjectTopicIdentifier.split('#');
            return { subject: parts[1] as Subject };
          },
        },
      }
    ),

    // Merge missedMarks into inaccuracyMarks for each analytics sub-object on decode
    createTransformer<typeof StudentSubjectAnalyticsSchema, typeof DBStudentSubjectAnalyticsSchema>(
      {
        decoder: {
          sourceFields: StudentSubjectAnalyticsAttributes.Analytics,
          decode: data => {
            const result: Record<string, unknown> = {};
            const record = data as Record<
              string,
              { inaccuracyMarks?: number; missedMarks?: number } | undefined
            >;
            for (const key of StudentSubjectAnalyticsAttributes.Analytics) {
              if (record[key]) {
                result[key] = mergeMissedMarks(record[key]);
              }
            }
            return result;
          },
        },
      }
    ),
  ],
};

export const encodeStudentSubjectAnalyticsToDb = createEncodeToDbFn(
  StudentSubjectAnalyticsCodecConfig
);
export const decodeStudentSubjectAnalyticsFromDb = createDecodeFromDbFn(
  StudentSubjectAnalyticsCodecConfig
);
